import Image from 'next/future/image';
import upIcon from '@/public/icons/faq/up.svg';
import downIcon from '@/public/icons/faq/down.svg';
import settings from '@/settings';
import Accordion from '../commons/Accordion';

type FaqType = {
  title: string;
  description: string;
};

const DefaultFAQsData = [
  {
    title: `What is Reebelo?`,
    description: `<p class='mb-4'>Reebelo is refreshing the way people consume tech; and providing affordable, functional and sustainable electronics is what we do.</p> <p class='mb-4'>Our buyback program is one of the many ways we’re building the circular economy. We’ve partnered with leading ${settings.country_citizen} experts in the refurbished space to bring you the very best prices for your pre-loved device.</p> <p>Get paid to give your devices a second-life with 3 simple steps! Get started here.</p>`,
  },
  {
    title: `Can I trust Reebelo?`,
    description: `<p class='mb-4'>Customer satisfaction is our #1 priority but don’t take our word for it. We have a 4.7 rating on trustpilot and check out what our awesome customers say <a class="text-blue-500" href='reviews'>here</a>.</p><p> We only work with buyback partners who have leading industry expertise to ensure a smooth buyback experience. Each partner is vetted and we work closely with them to offer competitive pricing, safe shipping methods, and secure payments. Our process is simple and we'll update you every step of the way.</p>`,
  },
  {
    title: `What happens if there's additional faults with the phone?`,
    description: `<p class='mb-4'>During the buyback process, we'll provide options for you to list any faults. Please fill in this part as accurately as you can to avoid any delays.</p> <p>Our certified partners will inspect and review every phone received. If we find that the phone is not as described, we'll contact you and offer a revised price.</p>`,
  },
  {
    title: `What happens to my phone data?`,
    description: `We'll completely wipe the data from your phone so your privacy is protected. Before sending in your device, we ask that you factory reset it, clean off any dust/grime, and remove any passcodes or iCloud/Google accounts.`,
  },
  {
    title: `Does the quoted price change?`,
    description: `<p class='mb-4'>We pride ourselves in helping you to find the best prices available in ${settings.country}. The prices quoted on our website are aggregated from different vendors so you can pick the best option for you.</p><p> Once you confirm the order, the price will be locked in for <span class='font-bold'>7 days</span>. Tech prices change constantly and we can only guarantee your quote during this period. After 7 days, the quotation may be subject to change.</p>`,
  },
  {
    title: `How do I send my phone in?`,
    description: `We want to make this process as simple as we can! We'll provide a free shipping label via email for you to print off and post the device.`,
  },
  {
    title: `What do you do with all the devices you buy?`,
    description: `<p class='mb-4'>Did you know that we produce over 50 MILLION tonnes of e-waste per year? The beauty of buyback is that you get to be part of the circular economy to help reduce e-waste!</p> <p>Your devices will get a second-life in the hands of another wonderful person. Our buyback partners will repair, clean and test all devices before reselling. For phones beyond repair, we'll recycle as much as we can, safely under the regulations set by the ${settings.country_citizen} government.</p>`,
  },
  {
    title: `How fast do I get my money?`,
    description: `<p class='mb-4'>Get paid fast! Once the device reaches our buyback partner, they'll inspect it within 1-2 business days. After inspection is completed, we'll initiate payment within 24 hours after confirmation of your device's condition.</p><p class='mb-4'> Please allow up to 1-7 business days for the funds to show up in your account depending on your chosen payment method.</p><p> If you have any questions or concerns, our team is always here to help! Use the live chat on our website, email us at <a class="text-blue-500 duration-300 ease-in-out" href="${settings.support_email}" target="_blank" rel="noreferrer"> ${settings.support_email}</a></p>`,
  },
];

const SGFAQsData = [
  {
    title: `What is Reebelo?`,
    description: `<p class='mb-4'>Reebelo is refreshing the way people consume tech; and providing affordable, functional and sustainable electronics is what we do.</p> <p class='mb-4'>Our buyback program is one of the many ways we’re building the circular economy. We’ve partnered with leading ${settings.country_citizen} experts in the refurbished space to bring you the very best prices for your pre-loved device.</p> <p>Get paid to give your devices a second-life with 3 simple steps! Get started here.</p>`,
  },
  {
    title: `Can I trust Reebelo?`,
    description: `<p class='mb-4'>Reebelo's mission is to professionalize and bring trust to the 2nd hand market. We are a registered Second Hand Goods Dealer with Singapore Police Force and hold IMDA's Telecommunication Dealer's license. But most importantly, you can also trust us to find the best deal to sell your new or used device as we work together with several buyback partners in Singapore!</p>`,
  },
  {
    title: `When do I get paid?`,
    description: `Our partners will directly pay you on the spot, no matter if you choose to drop off your device or convenient pickup with doorstep payment. In case that our experts cannot confirm the quoted price after the final expectation, you will directly receive a competitive counter-offer which you can either accept or simply keep your device otherwise.`,
  },
  {
    title: `How does the doorstep pickup process work?`,
    description: `In case that you choose our hassle-free pickup from your doorstep, you can directly submit via our web form your preferred time & address where our operations partner shall pick up your device. After a quick check, you get directly paid at your doorstep. In the unlikely case that the price we can offer is lower than the online quote, you can choose to accept our offer or to keep your device.`,
  },
  {
    title: `How does the dropoff process work?`,
    description: `After getting a quote online, you can directly schedule an appointment to drop off your device at one of our offline partner stores across the island. Please reach on time to ensure that our technician has the time when you arrive at our partner store. After your booking, we will send you detailed instructions on how to best get there via email. There will be a quick on-site check of your device and you will be paid directly on the spot.`,
  },
  {
    title: `How do you estimate the price of my device?`,
    description: `We partner with several major buyback partners across Singapore to make sure that you get a competitive and fair price when you sell your device. To get a quote, you just need to complete a few questions about the condition of your device. We automatically find out who offers the highest price and you instantly get a quote. After a brief on-site check, your quote will be confirmed. For the unlikely case that the final quote is different from the online quote, you can choose to sell for the competitive offer you will get, or you can keep your device if it doesn't match your expectations.`,
  },
  {
    title: `What devices can I sell with Reebelo?`,
    description: `You can find all the Phone, Tablets, Laptop & Smartwatch which our vendors currently buyback here. In case that your device isn’t listed, we currently don’t buy them back, unfortunately.`,
  },
  {
    title: `How do I change or cancel my appointment?`,
    description: `After booking an appointment, you will receive an email confirmation with the attached link to your appointment. On that page, you can any time change or cancel your appointment. As prices tend to fluctuate, we do not recommend canceling and rebooking your appointment to avoid the prices dropped since your last booking.`,
  },
  {
    title: `How do different conditions affect the quote?`,
    description: `Reebelo is committed to getting you the highest prices for your device regardless of your device's conditions! Make sure to answer all questions as honestly as possible to avoid price differences during the on-site check. Please note that the quote provided is an indicative quote based on the given condition of the device but the final quote will be given after our partners have inspected the device physically during your appointments.`,
  },
  {
    title: `Is my data protected?`,
    description: `Each device that is bought back through Reebelo must undergo a full data wipe. Reebelo's partners are professional refurbishers registered with the Singapore Police Force who must respect PDPA and user privacy regulations. In any case, we always advise you to the first backup, and then delete all data and remove all accounts from your device before selling it. Please note that once sold, we will not be able to retrieve any data that you have forgotten to backup.`,
  },
  {
    title: `Where is your shop located?`,
    description: `Reebelo is a Singapore based company. We work together with all major buyback & trade-in partners across Singapore to find you the best offer for your new or used device that you wish to sell. You will find Reebelo certified partner stores across the island. Please note that you have to use the webform above prior to selling to ensure that a technical team-member is available when you arrive.`,
  },
];
const USFAQsData = [
  {
    title: `What is Reebelo?`,
    description: `<p class='mb-4'>Reebelo is refreshing the way people consume tech; and providing affordable, functional, and sustainable electronics is what we do. Our buyback program is one of the many ways we're building the circular economy. We've partnered with leading experts in the refurbished space to bring you the best prices for your pre-loved device. Get paid to give your devices a second life with 3 simple steps! Get started here.</p>`,
  },
  {
    title: `Can I trust Reebelo with my device?`,
    description: `<p class='mb-4'>Customer satisfaction is our #1 priority but don't take our word for it. We have a 4.7 rating on Trustpilot and check out what our awesome customers say here.
    We only work with buyback partners who have leading industry expertise to ensure a smooth buyback experience. Each partner is vetted and we work closely with them to offer competitive pricing, safe shipping methods, and secure payments. Our process is simple and we'll update you every step of the way.
    </p>`,
  },
  {
    title: `Is the preliminary quote final?`,
    description: `The preliminary quote is not final. It is dependent on in-person inspection once your device reaches the buyback vendor’s facility.`,
  },
  {
    title: `What happens if there are additional faults with the device?`,
    description: `If the condition of the device is as described in the form, your quote will remain unchanged. If additional faults are noticed, the vendor may provide a new quote for your review.`,
  },
  {
    title: `What if I don’t agree with the amended offer?`,
    description: `If the amended offer isn’t what you expected, you can decline it. In this case, your device will be shipped back to you.`,
  },
  {
    title: `When and how do I get paid?`,
    description: `<p>Get paid fast! Once the device reaches our buyback partner, they'll inspect it within 1-2 business days. After the inspection is completed, we'll initiate payment via Zelle or Paypal within 48 hours after confirmation of your device's condition. Please allow up to 1-3 business days for the funds to show up in your account depending on your chosen payment method.</p>
    If you have any questions or concerns, our team is always here to help! Use the live chat on our website or email us at support@reebelo.com.
    `,
  },
  {
    title: `What devices can I sell with Reebelo?`,
    description: `You can find smartphones, tablets, laptops & smartwatches which our vendors currently buyback here. In case your device isn’t listed, we currently don’t buy them back.`,
  },
  {
    title: `What happens to my device data?`,
    description: `We'll completely wipe the data from your device so your privacy is protected.`,
  },
  {
    title: `How do I send my device in?`,
    description: `We want to make this process as simple as we can! We'll provide a free shipping label via email for you to print off and post the device.`,
  },
  {
    title: `What do you do with all the devices you buy?`,
    description: `Did you know, we produce over 50 MILLION tonnes of e-waste per year? The beauty of buyback is that you get to be part of the circular economy to help reduce e-waste!`,
  },
  {
    title: `Can I track the device I ship?`,
    description: `Yes, you will get a tracking code with the free shipping label. We will also inform you via email when the device reaches the vendor’s facility.`,
  },
];

const SectionContent = (props: { description: string }) => (
  <div className="rounded-b-[3px] border border-gray-700/[1] p-4 text-sm text-gray-700 md:px-8 md:py-5 md:text-base">
    {props.description && (
      <div
        className="text-sm text-gray-700 md:text-sm xl:text-base"
        // eslint-disable-next-line react/no-danger
        dangerouslySetInnerHTML={{ __html: props.description }}
      ></div>
    )}
  </div>
);

const SectionHeader = (props: {
  data: { title: string };
  isOpen?: boolean;
}) => {
  let headerCss = 'text-gray-700';

  if (props.isOpen) {
    headerCss =
      'font-bold bg-gray-700 !text-white border-gray-700 rounded-b-[0px]';
  } else {
    headerCss = 'bg-gray-300 border-gray-500 ';
  }

  return (
    <h3
      className={`${headerCss} relative block w-full rounded-[3px] border px-4 py-3 text-left text-base text-gray-700 md:px-8 md:py-5`}
    >
      <div className="w-full pr-10 text-left md:pr-0">{props.data.title}</div>
      <div className="absolute right-8 top-0 flex h-full w-[15px] items-center xs:w-[17px]">
        <Image
          src={props.isOpen ? upIcon : downIcon}
          loading="eager"
          alt={props.data.title}
        />
      </div>
    </h3>
  );
};

export default function Faq() {
  const endpoints = {
    quista: SGFAQsData,
    'reebelo-us': USFAQsData,
    default: DefaultFAQsData,
  };

  const faqContent = (endpoints as any)[settings.store];

  return (
    <>
      <h2 className="mb-3 text-center text-xl font-bold text-gray-700 xs:mb-5 xs:text-2xl sm:text-3xl md:mb-10">
        Frequently Asked Questions
      </h2>

      {(faqContent || []).map((s: FaqType, i: number) => (
        <Accordion
          as="div"
          key={i}
          titleComponent={<SectionHeader data={s} />}
          buttonClassName="w-full mt-4 md:mt-6 text-white"
        >
          <SectionContent description={s.description} />
        </Accordion>
      ))}
    </>
  );
}
